import React, { useEffect, useMemo, useState } from 'react';

import { clone, uniq, upperFirst } from 'lodash';
import { DateTime } from 'luxon';
import { MdErrorOutline } from 'react-icons/md';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { useGetLookupPersonPositionsQuery } from '@modules/lookups/api';
import {
  FeaturePermissionPositions,
  PanelTitle,
} from '@modules/permissionManager/constants';
import { useUnitRosterAdultsWithPositions } from '@modules/permissionManager/duck/hooks';
import {
  MultipleAssignFuncional,
  MultipleExpireFuncional,
} from '@modules/permissionManager/types';
import {
  usePostAssignFunctionalPositionsMutation,
  usePostExpireFunctionalPositionsMutation,
} from '@modules/rtk/esb-api';
import { Button, Collapse, Form, T, Tooltip } from '@modules/shared/components';
import { FormInstance } from '@shared/components/Form/Form.types';
import { unitTypes } from '@shared/constants';
import {
  isMobileSel,
  organizationGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { toastService } from '@toasts';

import { getPermisionPositionByPanelType } from '../utils';
import styles from './../permissionsTab.less';
import { PermissionsPanel } from './PermissionsPanel';

const { Panel } = Collapse;
interface PermissionsCollabsableProps {
  panelType: PanelTitle;
  form: FormInstance;
  searchGlobalTxt?: string;
  globalPositionsFilter?: string[];
}

const PermissionsCollabsable: React.FC<PermissionsCollabsableProps> = ({
  panelType,
  form,
  searchGlobalTxt = '',
  globalPositionsFilter,
}) => {
  const isMobile = useSelector(isMobileSel);

  const orgGuid = useSelector(organizationGuidSel) as string;
  const [assignFunctionalPosition, { isLoading: isSavingFunctionalPositon }] =
    usePostAssignFunctionalPositionsMutation();
  const [expireFunctionalPosition, { isLoading: isExpiringFunctionalPositon }] =
    usePostExpireFunctionalPositionsMutation();
  const { adultsData, youthsData, loading, unitType } =
    useUnitRosterAdultsWithPositions();
  const { data: personPositionsLookup, isFetching: loadingLookUp } =
    useGetLookupPersonPositionsQuery();

  const [checkedPerms, setCheckedPerms] = useState<Record<string, number[]>>(
    {},
  );
  const [originalCheckedPerms, setOriginalCheckedPerms] = useState<
    Record<string, number[]>
  >({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const isSaving = isSavingFunctionalPositon || isExpiringFunctionalPositon;

  const isYouthPanelType = useMemo(
    () => panelType.includes('Youth'),
    [panelType],
  );

  const originalData = useMemo(
    () => (isYouthPanelType ? youthsData : adultsData),
    [youthsData, adultsData, isYouthPanelType],
  );

  const isYouthAdvancementDifferentToTroop = useMemo(
    () =>
      panelType === PanelTitle.ADVANCEMENT_YOUTH &&
      unitType !== unitTypes.TROOP,
    [unitType, panelType],
  );

  const featurePermissionPositionsWithId = useMemo(() => {
    const updatedEntry: Record<string, { id: number; name: string }> = {};
    if (!personPositionsLookup) return undefined;
    Object.entries(FeaturePermissionPositions).forEach(([key, value]) => {
      updatedEntry[key] = value;
      if (value.name) {
        const foundPosition = personPositionsLookup.find(
          pos => pos.name === value.name,
        );
        updatedEntry[key].id = foundPosition ? +foundPosition.id : value.id;
        updatedEntry[key].name = foundPosition
          ? foundPosition.name
          : value.name;
        return;
      }
    });
    return updatedEntry;
  }, [personPositionsLookup]);

  // Initialize the checked values
  useEffect(() => {
    if (!featurePermissionPositionsWithId) return;

    const assignablePositionsIds = getPermisionPositionByPanelType(
      featurePermissionPositionsWithId,
    )[panelType];

    const originalCheckedPermissions: Record<string, number[]> = {};
    originalData.forEach(user => {
      const userPermsPositions = user.registrationData.filter(reg =>
        assignablePositionsIds.includes(reg.positionId),
      );
      if (userPermsPositions.length) {
        userPermsPositions.forEach(pos => {
          if (!originalCheckedPermissions[user.personGuid]?.length) {
            originalCheckedPermissions[user.personGuid] = [pos.positionId];
          } else {
            originalCheckedPermissions[user.personGuid].push(pos.positionId);
          }
        });
      }
      setCheckedPerms(originalCheckedPermissions);
      setOriginalCheckedPerms({ ...originalCheckedPermissions });
    });
  }, [originalData, panelType, featurePermissionPositionsWithId]);

  const handleClickSelectRecommended = (
    recommendedCells: {
      celKey: string;
      positionId: number;
      personGuid: string;
    }[],
  ) => {
    setHasChanges(true);
    setCheckedPerms(prevState => {
      recommendedCells.forEach(cell => {
        const foundRecord = clone(prevState[cell.personGuid]);
        if (foundRecord) {
          foundRecord.push(cell.positionId);
          prevState[cell.personGuid] = uniq(foundRecord);
        } else {
          prevState[cell.personGuid] = [cell.positionId];
        }
      });
      return prevState;
    });
  };

  const handleRemoveRecommended = (
    recommendedCells: {
      celKey: string;
      positionId: number;
      personGuid: string;
    }[],
  ) => {
    setHasChanges(true);
    setCheckedPerms(prevState => {
      recommendedCells.forEach(cell => {
        const foundRecord = prevState[cell.personGuid];
        if (foundRecord) {
          prevState[cell.personGuid] = foundRecord.filter(
            recordPosId => recordPosId !== cell.positionId,
          );
        }
      });
      return prevState;
    });
  };

  const handleApply: React.MouseEventHandler<HTMLElement> = async e => {
    e.stopPropagation();
    try {
      const getExpDt = (personGuid: string) => {
        const foundPerson = originalData.find(
          person => person.personGuid === personGuid,
        );
        if (foundPerson?.registrationData.length) {
          return foundPerson?.registrationData[0].registrationExpireDt;
        }
        return DateTime.now().endOf('year').toISODate();
      };

      const functionalAssignPayload: MultipleAssignFuncional[] = [];
      Object.entries(checkedPerms).forEach(([key, checkedIds]) => {
        const userOriginalCheckedPerms = originalCheckedPerms[key] || [];
        const idsToAdd = checkedIds.filter(
          id => !userOriginalCheckedPerms.find(origId => origId === id),
        );
        idsToAdd.forEach(id => {
          functionalAssignPayload.push({
            personGuid: key,
            body: {
              PositionId: id,
              OrganizationGuid: orgGuid,
              EffectiveDt: DateTime.now().toISODate(),
              ExpiryDt: getExpDt(key),
            },
          });
        });
      });

      const functionalExpirePayload: MultipleExpireFuncional[] = [];
      Object.entries(originalCheckedPerms).forEach(
        ([key, originalCheckedIds]) => {
          const userCheckedPerms = checkedPerms[key];
          const idsToDelete = originalCheckedIds.filter(
            origId => !userCheckedPerms.find(id => origId === id),
          );
          idsToDelete.forEach(id => {
            const pkPersonPositionId = originalData
              .find(user => user.personGuid === key)
              ?.registrationData.find(
                reg => reg.positionId === id,
              )?.personPositionId;

            if (pkPersonPositionId) {
              functionalExpirePayload.push({
                personGuid: key,
                body: {
                  PositionId: id,
                  PkPersonPositionId: pkPersonPositionId,
                  OrganizationGuid: orgGuid,
                  ExpiryDt: DateTime.now().toISODate(),
                },
              });
            }
          });
        },
      );

      if (functionalExpirePayload.length) {
        await Promise.all(
          functionalExpirePayload.map(payload =>
            expireFunctionalPosition({
              personGuid: payload.personGuid,
              body: payload.body,
              shouldInvalidateTags: !functionalAssignPayload.length,
              isYouth: isYouthPanelType,
            }).unwrap(),
          ),
        );
      }

      if (functionalAssignPayload.length) {
        await Promise.all(
          functionalAssignPayload.map(payload =>
            assignFunctionalPosition({
              personGuid: payload.personGuid,
              body: payload.body,
              shouldInvalidateTags: true,
              isYouth: isYouthPanelType,
            }).unwrap(),
          ),
        );
      }
      setHasChanges(false);
      toastService.success(
        <FormattedMessage id="permissionManager.permissionTab.successMessage" />,
      );
    } catch (error) {
      if (error?.data?.errorDesc) toastService.error(error.data.errorDesc);
    }
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasChanges(true);
    const { target } = e;
    const { checked } = target;
    const dataset = target.dataset as unknown as string;

    const [personGuid, positionId] = dataset.split('_');
    setCheckedPerms(prevState => {
      const arrayToUpdate = prevState[personGuid]
        ? clone(prevState[personGuid])
        : [];

      if (checked) {
        arrayToUpdate.push(+positionId);
        const updatedIds = uniq(arrayToUpdate);
        prevState[personGuid] = updatedIds;
        return prevState;
      }
      const updatedIds = arrayToUpdate.filter(id => id !== +positionId);
      prevState[personGuid] = updatedIds;
      return prevState;
    });
  };

  return (
    <Collapse
      expandIconPosition="right"
      bordered={false}
      className={styles.collapse}
      defaultActiveKey={panelType}
    >
      <Panel
        header={
          <div className={styles.panelHeader}>
            <div className={styles.panelHeaderTitleContainer}>
              <T noMargin colored bold size="2">
                {
                  <FormattedMessage
                    id="permissionManager.permissionTab.panelTitle"
                    values={{
                      panelTitle: upperFirst(
                        panelType.replace(
                          '(Youth)',
                          '(Permissions Assignable to Youth)',
                        ),
                      ),
                    }}
                  />
                }
              </T>
              <Tooltip
                title={
                  <FormattedHTMLMessage
                    id={`permissionManager.permissionTab.tooltip.${panelType}`}
                  />
                }
                placement={isMobile ? 'bottom' : 'right'}
                trigger={isMobile ? 'click' : 'hover'}
                overlayStyle={{
                  maxWidth: '50vw',
                }}
              >
                <MdErrorOutline onClick={e => e.stopPropagation()} />
              </Tooltip>
            </div>
          </div>
        }
        key={panelType}
        className={styles.panel}
      >
        {isYouthAdvancementDifferentToTroop ? (
          <div style={{ padding: '8px' }}>
            <FormattedMessage id="permissionManager.permissionTab.warning.youthAdvancements" />
          </div>
        ) : (
          <div className={styles.panelContainer}>
            <PermissionsPanel
              searchGlobalTxt={searchGlobalTxt}
              globalPositionsFilter={globalPositionsFilter}
              form={form}
              tableData={isYouthPanelType ? youthsData : adultsData}
              featurePermissionPositionsWithId={
                featurePermissionPositionsWithId
              }
              loading={
                loading ||
                isSaving ||
                !featurePermissionPositionsWithId ||
                loadingLookUp
              }
              unitType={unitType}
              panelType={panelType}
              onChangeCheckbox={handleCheck}
              onClickSelectRecommended={handleClickSelectRecommended}
              onClickRemoveRecommended={handleRemoveRecommended}
            />
            <div className={styles.panelFooter}>
              <Button
                shape="round"
                type="primary"
                size="large"
                uppercase={false}
                loading={isSaving}
                disabled={!hasChanges}
                onClick={handleApply}
              >
                <FormattedMessage id="permissionManager.permissionTab.applyChanges" />
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </Collapse>
  );
};

export default Form.create<PermissionsCollabsableProps>()(
  PermissionsCollabsable,
);
